import React, { useRef, useCallback, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { styled } from "@mui/material/styles";
import QRCode from "react-qr-code";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ShareQRModal from "./ShareQRModal";

const QRFrame = styled(Box)(({ theme }) => ({
  background: "#fff",
  borderRadius: 16,
  padding: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  border: "1px solid #F0F0F0",
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    maxWidth: 320,
    margin: "0 auto",
  },
}));

const ActionButton = styled(Button)(({ variant: v }) => ({
  borderRadius: 12,
  padding: "12px 0",
  fontWeight: 600,
  fontSize: 14,
  textTransform: "lowercase",
  flex: 1,
  fontFamily: "Inter, Arial, sans-serif",
  ...(v === "outlined"
    ? {
        borderColor: "#E0E0E0",
        color: "#333",
        backgroundColor: "#F5F5F5",
        "&:hover": { backgroundColor: "#EBEBEB", borderColor: "#D0D0D0" },
      }
    : {
        backgroundColor: "#FFFAED",
        color: "#F2A73B",
        border: "1px solid #FFE8B0",
        "&:hover": { backgroundColor: "#FFF3D0" },
      }),
}));

const LinkDisplayBox = styled(Box)({
  background: "#F5F5F5",
  borderRadius: 12,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
});

const QRDetailView = ({ qr, onBack, isDesktop, onRegenerate }) => {
  const qrRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const menuOpen = Boolean(menuAnchor);

  const handleCopyLink = useCallback(() => {
    const url = qr.qrValue || qr.url || "";
    navigator.clipboard
      ?.writeText(url)
      .then(() => toast.success("link copied"))
      .catch(() => toast.error("failed to copy link"));
    setMenuAnchor(null);
  }, [qr]);

  const handleCopyQR = useCallback(() => {
    const url = qr.qrValue || qr.url || "";
    navigator.clipboard
      ?.writeText(url)
      .then(() => toast.success("qr copied"))
      .catch(() => toast.error("failed to copy qr"));
  }, [qr]);

  const handleDownload = useCallback(() => {
    const svgEl = qrRef.current?.querySelector("svg");
    if (!svgEl) return;

    const locationName = qr.locationId?.businessLocationName || qr.locationName || "location";
    const fileName = `${locationName.replace(/\s+/g, "_")}_qr.png`;

    const svgData = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const padding = 32;
      const size = img.width + padding * 2;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      // White background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, padding, padding, img.width, img.height);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        URL.revokeObjectURL(svgUrl);
      }, "image/png");
    };
    img.src = svgUrl;
    setMenuAnchor(null);
  }, [qr]);

  const handleRegenerate = useCallback(() => {
    setMenuAnchor(null);
    onRegenerate?.(qr, {
      onSuccess: () => toast.success("qr code regenerated"),
      onError: () => toast.error("failed to regenerate qr code"),
    });
  }, [onRegenerate, qr]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FAFAFA",
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: 1,
          bgcolor: "#A259FF",
          borderRadius: isDesktop ? "0 12px 0 0" : 0,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={onBack}
            size="small"
            sx={{ color: "#fff", p: 0.5 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            fontWeight={600}
            fontSize={16}
            color="#fff"
            sx={{ fontFamily: "Inter, Arial, sans-serif", textTransform: "lowercase" }}
          >
            location qr
          </Typography>
        </Box>
        <Tooltip title="more options">
          <IconButton
            size="small"
            sx={{ color: "#fff" }}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="more options"
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2.5,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* QR Code */}
        <QRFrame ref={qrRef}>
          <QRCode
            value={qr.qrValue}
            size={isDesktop ? 220 : 200}
            style={{ width: "100%", maxWidth: isDesktop ? 220 : 200, height: "auto" }}
          />
        </QRFrame>

        {/* Location info */}
        <Box textAlign="center">
          <Typography
            fontWeight={700}
            fontSize={18}
            sx={{ fontFamily: "Inter, Arial, sans-serif" }}
          >
            {qr.locationId?.businessLocationName || qr.locationName}
          </Typography>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            gap={0.5}
            mt={0.5}
            sx={{ maxWidth: 280, mx: "auto" }}
          >
            <LocationOnOutlinedIcon
              sx={{ fontSize: 14, color: "#666", flexShrink: 0, mt: "2px" }}
            />
            <Typography
              fontSize={13}
              color="#666"
              sx={{ fontFamily: "Inter, Arial, sans-serif", textAlign: "left", lineHeight: 1.5 }}
            >
              {qr.locationId?.physicalAddress || qr.city}
            </Typography>
          </Box>
        </Box>

        {/* Link display */}
        <LinkDisplayBox>
          <LinkIcon sx={{ fontSize: 18, color: "#A259FF", flexShrink: 0 }} />
          <Typography
            fontSize={13}
            color="#A259FF"
            noWrap
            flex={1}
            sx={{ fontFamily: "Inter, Arial, sans-serif" }}
          >
            {qr.shortUrl}
          </Typography>
          <Tooltip title="copy link">
            <IconButton size="small" onClick={handleCopyLink} aria-label="copy link">
              <ContentCopyOutlinedIcon sx={{ fontSize: 16, color: "#999" }} />
            </IconButton>
          </Tooltip>
        </LinkDisplayBox>

        {/* Primary actions */}
        <Box display="flex" gap={1.5} width="100%">
          <ActionButton
            variant="outlined"
            startIcon={<ShareOutlinedIcon />}
            onClick={() => setShareOpen(true)}
          >
            share qr
          </ActionButton>
          <ActionButton
            startIcon={<ContentCopyOutlinedIcon />}
            onClick={handleCopyQR}
          >
            copy qr
          </ActionButton>
        </Box>
      </Box>

      {/* Three-dot menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 160,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            "& .MuiMenuItem-root": {
              fontSize: 14,
              textTransform: "lowercase",
              fontFamily: "Inter, Arial, sans-serif",
              gap: 1.5,
              py: 1.2,
            },
          },
        }}
      >
        <MenuItem onClick={handleCopyLink}>
          <LinkIcon sx={{ fontSize: 18, color: "#999" }} />
          copy link
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <FileDownloadOutlinedIcon sx={{ fontSize: 18, color: "#999" }} />
          download qr
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleRegenerate} sx={{ color: "#F2A73B !important" }}>
          <AutorenewIcon sx={{ fontSize: 18, color: "#F2A73B" }} />
          regenerate qr
        </MenuItem>
      </Menu>

      {/* Share modal */}
      <ShareQRModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        qr={qr}
        onCopied={() => toast.success("link copied")}
      />
    </Box>
  );
};

export default QRDetailView;
